import React, { useState } from 'react';
import { FiMapPin, FiChevronDown } from 'react-icons/fi';
import { WidgetDef } from '../types';
import { DemoHead, useWidgetSite } from '../shared';
import { MiniCode } from '../WidgetDetail';

// spec 623:5001 — no settings; "One Code per Detail" takes the Settings card's place
const DETAIL_CODES = [
    { label: 'Registered office', field: 'registered-office' },
    { label: 'Postal address', field: 'postal-address' },
    { label: 'Phone', field: 'phone' },
    { label: 'Email', field: 'email' },
    { label: 'ACN', field: 'acn' },
    { label: 'ABN', field: 'abn' },
    { label: 'ASX code', field: 'asx-code' },
    { label: 'Share registry', field: 'share-registry' },
    { label: 'Auditor', field: 'auditor' },
    { label: 'Solicitors', field: 'solicitors' },
    { label: 'Bankers', field: 'bankers' },
    { label: 'Social media links', field: 'social' },
];

// collapses like the other right-hand cards (WidgetDetail's Collapsible)
const OneCodePerDetail: React.FC = () => {
    const [open, setOpen] = useState(true);
    return <section className={`card wd-collapsible wd-one-code${open ? '' : ' closed'}`}>
        <button type="button" className="acc-head" aria-expanded={open} onClick={() => setOpen(!open)}>
            <h2>One Code per Detail</h2> <FiChevronDown className="chev" />
        </button>
        {open && <>
            <p className="wd-muted">Drop a single detail into a footer, a contact page or a sidebar — it shows just that value, with no table around it.</p>
            <div className="wd-mini-codes">
                {DETAIL_CODES.map(d => <MiniCode key={d.field} label={d.label} code={`[investi_company field="${d.field}"]`} />)}
            </div>
            <p className="wd-muted">Social media links come from Website Hub › Company details. Turning a detail off there removes it from the table and from its own code.</p>
        </>}
    </section>;
};

const companyDetails: WidgetDef = {
    slug: 'company-details',
    name: 'Company details',
    description: 'Registered office, registry, exchange, auditor and contacts',
    icon: <FiMapPin />,
    title: 'Company Details Widget',
    installed: true,
    page: '/investors/corporate-directory/',
    views: 260,
    demoPath: '/investors/corporate-directory/',
    addNote: 'Add the whole directory with one code, or place single details anywhere on the page using the codes below. Every code stays in step with what you enter in Website Hub.',
    addBlocks: [
        { title: '1 · The whole directory', code: '[investi_company_details instance="company-details"]' },
        { title: '2 · Or plain HTML', code: '<div data-investi-instance="company-details" data-investi="company-details"></div>', dark: true },
    ],
    Side: OneCodePerDetail,
    Demo: () => {
        const { host, ticker } = useWidgetSite();
        const rows: [string, string[]][] = [
            ['Registered office', ['Level 2, 100 Example Street, West Perth WA 6005']],
            ['Phone', ['+61 8 9000 0000']],
            ['Email', [`investors@${host}`]],
            ['ACN / ABN', ['ACN 600 000 000 · ABN 12 600 000 000']],
            ['Stock exchange', ['Australian Securities Exchange (ASX)', `Code: ${ticker} · listed 14 March 2019`]],
            ['Share registry', ['Automic Group', 'Level 5, 126 Phillip Street, Sydney NSW 2000 · 1300 288 664']],
            ['Auditor', ['Example Audit Partners', 'Level 8, 200 Sample Street, Perth WA 6000']],
            ['Solicitors', ['Placeholder Legal', 'Level 12, 45 Example Terrace, Perth WA 6000']],
            ['Bankers', ['Sample Banking Corporation', '300 Example Street, Perth WA 6000']],
            ['Follow us', ['LinkedIn · X · YouTube']],
        ];
        return <div className="wd-company">
            <DemoHead title="Corporate directory" sub="Who to contact and where we are" />
            <div className="wd-directory">
                {rows.map(([label, lines]) => <div key={label} className="tr">
                    <span className="l">{label}</span>
                    <span className="v">{lines.map(l => <span key={l}>{l}</span>)}</span>
                </div>)}
            </div>
        </div>;
    },
};

export default companyDetails;
